import { useEffect, useRef, type RefObject } from 'react';

export function useEventListener<K extends keyof WindowEventMap>(
  type: K,
  handler: (e: WindowEventMap[K]) => void,
  target?: Window | null
): void;
export function useEventListener<K extends keyof DocumentEventMap>(
  type: K,
  handler: (e: DocumentEventMap[K]) => void,
  target: Document
): void;
export function useEventListener<K extends keyof HTMLElementEventMap>(
  type: K,
  handler: (e: HTMLElementEventMap[K]) => void,
  target: HTMLElement | RefObject<HTMLElement | null>
): void;
export function useEventListener(
  type: string,
  handler: (e: Event) => void,
  target: EventTarget | RefObject<EventTarget | null> | null = typeof window !== 'undefined' ? window : null
): void {
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);
  useEffect(() => {
    const el =
      target && 'current' in (target as RefObject<EventTarget>) ? (target as RefObject<EventTarget>).current : (target as EventTarget | null);
    if (!el) return undefined;
    const listener = (e: Event) => handlerRef.current(e);
    el.addEventListener(type, listener);
    return () => el.removeEventListener(type, listener);
  }, [type, target]);
}

export function useOnClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  handler: (e: MouseEvent | TouchEvent) => void
): void {
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const listener = (e: MouseEvent | TouchEvent) => {
      const el = ref.current;
      if (!el || el.contains(e.target as Node)) return;
      handlerRef.current(e);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref]);
}

export function useKeyPress(key: string, handler: (e: KeyboardEvent) => void): void {
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const listener = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === key.toLowerCase()) handlerRef.current(e);
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [key]);
}
